/**
 * SAAS-CLAIM-UI — modal admin de códigos de vínculo (claim codes).
 *
 * super_admin/field: gera / lista / revoga códigos de vínculo de um device. O
 * código é exibido UMA única vez (só o hash fica no backend) com botão copiar.
 * Dispositivos protegidos (produção/gold/referência) bloqueiam a geração
 * visualmente (o backend também recusa com 403). allow_rebind só p/ super_admin.
 *
 * Endpoints já existentes: generateClaimCode / listClaimCodes / revokeClaimCode.
 * Este gate NÃO cria claim real em produção — é a superfície de UI.
 */
import { useCallback, useEffect, useState } from "react";
import { generateClaimCode, listClaimCodes, revokeClaimCode } from "../lib/api.js";
import { getProtectionState, protectionSourceLabel } from "../lib/protection.js";
import { isPlatformAdmin } from "../lib/rbac.js";
import {
  claimStateBadge, canRevoke, canAllowRebind, TTL_OPTIONS, fmtWhen,
} from "../lib/claimCodes.js";

const TONE_CLASS = {
  ok: "text-noc-ok border-noc-ok/40",
  muted: "text-noc-muted border-noc-border",
  warn: "text-noc-warn border-noc-warn/40",
  err: "text-noc-err border-noc-err/40",
};

export default function ClaimCodeManager({ deviceId, data, onClose, notify }) {
  const prot = getProtectionState(data);
  const platform = isPlatformAdmin();

  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // form
  const [ttl, setTtl] = useState(72);
  const [label, setLabel] = useState("");
  const [allowRebind, setAllowRebind] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null); // {code, expires_at,...}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = await listClaimCodes(deviceId);
      setCodes(out.codes || []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar códigos.");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  async function handleGenerate(e) {
    e.preventDefault();
    setError(null);
    setJustGenerated(null);
    setGenerating(true);
    try {
      const payload = { ttl_hours: ttl };
      if (label.trim()) payload.label = label.trim();
      if (platform && allowRebind) payload.allow_rebind = true;
      const out = await generateClaimCode(deviceId, payload);
      setJustGenerated(out);          // out.code exibido 1x
      setLabel("");
      setAllowRebind(false);
      notify?.("ok", "Código de vínculo gerado.");
      await load();
    } catch (e) {
      setError(e?.message || "Falha ao gerar código.");
    } finally {
      setGenerating(false);
    }
  }

  function copyCode(code) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => notify?.("ok", "Código copiado para a área de transferência."))
        .catch(() => notify?.("error", "Falha ao copiar."));
    } else {
      notify?.("error", "Clipboard indisponível no navegador.");
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm("Revogar este código? Ele deixará de funcionar imediatamente.")) return;
    setError(null);
    try {
      await revokeClaimCode(id);
      notify?.("ok", "Código revogado.");
      await load();
    } catch (e) {
      setError(e?.message || "Falha ao revogar código.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-2xl
                      max-h-[90vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <h2 className="text-base font-semibold text-noc-text">🔗 Códigos de vínculo</h2>
            <p className="text-[11px] text-noc-muted font-mono">{deviceId}</p>
          </div>
          <button onClick={onClose} className="btn-ghost h-8 px-3 text-sm">Fechar</button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded-lg px-3 py-2">
            <span className="led-err" />{error}
          </div>
        )}

        {/* código recém-gerado — exibido UMA vez */}
        {justGenerated?.code && (
          <div className="rounded-lg border border-noc-accent/40 bg-noc-accent/10 p-3 space-y-2">
            <div className="text-xs text-noc-text font-semibold">
              Envie este código ao cliente para vincular o dispositivo.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 select-all break-all font-mono text-sm text-noc-text
                               bg-noc-bg border border-noc-border rounded px-2 py-1.5">
                {justGenerated.code}
              </code>
              <button onClick={() => copyCode(justGenerated.code)}
                className="btn-primary h-9 px-3 text-sm shrink-0">Copiar</button>
            </div>
            <div className="text-[11px] text-noc-warn">
              ⚠️ O código é exibido somente uma vez. Expira em {fmtWhen(justGenerated.expires_at)}.
            </div>
          </div>
        )}

        {/* geração */}
        {prot.protected ? (
          <div className="rounded-lg border border-noc-warn/40 bg-noc-warn/10 p-3 text-sm text-noc-warn">
            🔒 Dispositivo protegido{prot.source ? ` (${protectionSourceLabel(prot.source)})` : ""} —
            geração de código de vínculo bloqueada (produção/gold/referência/canário/piloto).
          </div>
        ) : (
          <form onSubmit={handleGenerate}
            className="rounded-lg border border-noc-border p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <label className="label">Validade</label>
              <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}
                className="w-full h-9 px-2 rounded-lg bg-noc-bg border border-noc-border
                           text-noc-text focus:border-noc-accent/60 outline-none">
                {TTL_OPTIONS.map((o) => <option key={o.hours} value={o.hours}>{o.label}</option>)}
              </select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="label">Observação (opcional)</label>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120}
                className="w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border
                           text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="ex.: instalação cliente X" />
            </div>
            {platform && (
              <label className="flex items-center gap-2 text-xs text-noc-muted sm:col-span-3">
                <input type="checkbox" checked={allowRebind}
                  onChange={(e) => setAllowRebind(e.target.checked)} />
                Permitir revincular device já pertencente a outro tenant (super_admin)
              </label>
            )}
            <div className="sm:col-span-3">
              <button type="submit" disabled={generating} className="btn-primary h-9 px-4">
                {generating ? "Gerando…" : "Gerar código de vínculo"}
              </button>
            </div>
          </form>
        )}

        {/* lista de códigos existentes */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-noc-text">Códigos existentes</h3>
            <button onClick={load} className="btn-ghost h-7 px-2 text-xs">Atualizar</button>
          </div>
          <div className="card divide-y divide-noc-border">
            {loading && <div className="p-3 text-sm text-noc-muted">Carregando…</div>}
            {!loading && codes && codes.length === 0 && (
              <div className="p-3 text-sm text-noc-muted">Nenhum código gerado ainda.</div>
            )}
            {!loading && codes && codes.map((c) => {
              const b = claimStateBadge(c.state);
              return (
                <div key={c.id} className="p-3 flex items-center gap-3 flex-wrap text-sm">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${TONE_CLASS[b.tone] || TONE_CLASS.muted}`}>
                    {b.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-noc-text truncate">
                      {c.label || <span className="text-noc-muted">sem observação</span>}
                      {c.allow_rebind && <span className="ml-2 text-[10px] uppercase text-noc-warn">rebind</span>}
                    </div>
                    <div className="text-[11px] text-noc-muted">
                      criado {fmtWhen(c.created_at)} · expira {fmtWhen(c.expires_at)}
                      {c.used_at && ` · usado ${fmtWhen(c.used_at)}${c.used_by ? " por "+c.used_by : ""}`}
                    </div>
                  </div>
                  {canRevoke(c.state) && (
                    <button onClick={() => handleRevoke(c.id)} className="btn-ghost h-7 px-2 text-xs">
                      Revogar
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
